import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { LoginCredentials, LoginResponse, ApiError, PasswordResetConfirmPayload, PasswordResetRequestPayload } from '@/types/auth';
import { authUtils } from '@/lib/auth';
import { API_BASE_URL, API_TIMEOUT_MS } from '@/lib/constants';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length, cookie.length);
  }
  return null;
};
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});



export const setCookieValue = (name: string, value: string, days: number = 7) => {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

export const setAccessTokenCookie = (token: string, refreshTokenExpiry?: number) => {
  if (typeof document === 'undefined') return;
  
  if (refreshTokenExpiry) {
    const expires = new Date(Date.now() + (refreshTokenExpiry * 1000));
    document.cookie = `accessToken=${token};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } else {
    const tokenExpiry = getTokenExpiry(token);
    if (tokenExpiry) {
      const expires = new Date(tokenExpiry);
      document.cookie = `accessToken=${token};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    } else {
      setCookieValue('accessToken', token, 7);
    }
  }
};

export const getTokenExpiry = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp) {
      return payload.exp * 1000; 
    }
    return null;
  } catch (error) {
    console.error('[Token Utils] Error parsing token:', error);
    return null;
  }
};

export const isTokenExpiringSoon = (token: string): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true; 
  
  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const twoMinutes = 2 * 60 * 1000; 
  
  return timeUntilExpiry < twoMinutes;
};

// Narrower network error guard
export const isNetworkError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  return (
    e.code === 'ERR_NETWORK' ||
    e.code === 'ECONNABORTED' ||
    (typeof e.message === 'string' && (
      e.message.includes('Network Error') ||
      e.message.includes('timeout')
    ))
  );
};

api.interceptors.request.use(
  async (config) => {
    // Per-tab impersonation: use sessionStorage token so this tab acts as the customer
    const impToken = authUtils.getImpersonationToken();
    if (impToken) {
      config.headers.Authorization = `Bearer ${impToken}`;
      return config;
    }

    const token = getCookieValue('accessToken');

    // Proactive token refresh: Check if token is expiring soon or missing
    if (!config.url?.includes('/auth/')) {
      if (!token) {
        try {
          const refreshResponse = await api.post<{ accessToken: string; refreshTokenExpiry?: number }>('/auth/refresh');
          if (refreshResponse.data.accessToken) {
            setAccessTokenCookie(refreshResponse.data.accessToken, refreshResponse.data.refreshTokenExpiry);
            config.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          }
        } catch (refreshError: unknown) {
          if (isNetworkError(refreshError)) {
            console.warn('[Token Refresh] Network error during refresh');
          } else {
            console.error('[Token Refresh] Refresh failed:', refreshError);
          }
        }
      } else if (isTokenExpiringSoon(token)) {
        try {
          const refreshResponse = await api.post<{ accessToken: string; refreshTokenExpiry?: number }>('/auth/refresh');
          if (refreshResponse.data.accessToken) {
            setAccessTokenCookie(refreshResponse.data.accessToken, refreshResponse.data.refreshTokenExpiry);
            config.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          }
        } catch (refreshError: unknown) {
          if (isNetworkError(refreshError)) {
            console.warn('[Token Refresh] Network error during proactive refresh');
          } else {
            console.error('[Token Refresh] Proactive refresh failed:', refreshError);
          }
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                          originalRequest?.url?.includes('/auth/refresh') ||
                          originalRequest?.url?.includes('/auth/password-reset');
    
    if (error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint) {
      if (typeof window !== 'undefined' && authUtils.getImpersonationToken()) {
        authUtils.clearImpersonationToken();
        localStorage.removeItem('user');
        window.location.href = '/signin';
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      try {
        const refreshResponse = await api.post<{ accessToken: string; refreshTokenExpiry?: number }>('/auth/refresh');
        if (refreshResponse.data.accessToken) {
          setAccessTokenCookie(refreshResponse.data.accessToken, refreshResponse.data.refreshTokenExpiry);

          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError: unknown) {
        if (isNetworkError(refreshError)) {
          console.warn('[Token Refresh] Network error while refreshing. Keeping session.');
          return Promise.reject(error);
        }

        console.error('[Token Refresh] Refresh failed:', refreshError);

        if (typeof window !== 'undefined') {
          localStorage.removeItem('user');
          document.cookie = 'accessToken=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax';
          document.cookie = 'selectedBusinessUnit=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax';
          window.location.href = '/signin';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const responseData = axiosError.response?.data;
      throw {
        message: responseData?.message || 'Login failed',
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      console.warn('Logout request failed, but clearing local storage');
    }
  },

  async getCurrentUser(): Promise<unknown> {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const responseData = axiosError.response?.data;
      throw {
        message: responseData?.message || 'Failed to get user info',
        status: axiosError.response?.status,
      } as ApiError;
    }
  },

  async requestPasswordReset(payload: PasswordResetRequestPayload): Promise<{ emailExists: boolean; message: string }> {
    const response = await api.post<{ emailExists: boolean; message: string }>('/auth/password-reset/request', payload);
    return response.data;
  },

  async confirmPasswordReset(payload: PasswordResetConfirmPayload): Promise<void> {
    try {
      await api.post('/auth/password-reset/confirm', payload);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const responseData = axiosError.response?.data;
      throw {
        message: responseData?.message || 'Failed to reset password',
        status: axiosError.response?.status,
      } as ApiError;
    }
  },
};

export default api;

